import { Component, inject, computed, signal, HostListener, ElementRef, Input, effect, DestroyRef, ViewChild, TemplateRef, OnInit, OnChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SelectMenuComponent, SelectMode, SelectItem } from '../../../../shared/components/select-menu/select-menu.component';
import { DashboardService } from '../../../../services/dashboard.service';
import { CopilotService } from '../../../../services/copilot.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NO_COMPETITORS_ASSIGNED } from '../../../../utils/constants';
import { MatTooltip } from '@angular/material/tooltip';
import { POLARIS_NAME } from '../../copilot.model';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';


@Component({
    selector: 'app-two-stage-brand-picker',
    imports: [SelectMenuComponent, MatTooltip, AssetVersionPipe],
    templateUrl: './two-stage-brand-picker.component.html',
    styleUrl: './two-stage-brand-picker.component.scss'
})
export class TwoStageBrandPickerComponent implements OnChanges {
    @Input() placeholderText = '';
    @Input() competitorSelectionText = '';
    @Input() myBrand?: string;
    @Input() myCompetitors?: string[];
    @Input() maxCompetitors = 13;
    @ViewChild('brandHeaderTemplate') brandHeaderTemplate!: TemplateRef<any>;
    @ViewChild('competitorSelectMenu') competitorSelectMenu?: SelectMenuComponent;
    private dashboardService = inject(DashboardService);
    private copilotService = inject(CopilotService);
    private elementRef = inject(ElementRef);
    protected readonly SelectMode = SelectMode;
    protected readonly NO_COMPETITORS_ASSIGNED = NO_COMPETITORS_ASSIGNED;
    public brandControl = new FormControl<string | null>(null);
    public competitorControl = new FormControl<string[]>([]);
    public isPanelOpen = signal(false);
    public isCompetitorPanelOpen = signal(false);
    public brandControlValue = toSignal(this.brandControl.valueChanges, { initialValue: this.brandControl.value });
    public competitorControlValue = toSignal(this.competitorControl.valueChanges, { initialValue: this.competitorControl.value });
    public tooltipText = `Start a new chat to ask ${POLARIS_NAME} about other brands.`;
    public shouldAllowSelect = true;



    ngOnChanges(): void {
        this.brandControl.setValue(this.myBrand ?? null);
        this.competitorControl.setValue(this.myCompetitors ?? []);
        if (this.myBrand)
            this.shouldAllowSelect = false;
    }

    public brandOptions = computed<SelectItem[]>(() => {
        const brands = this.dashboardService.myBrandOptions();
        if (!brands) return [];
        return brands.map(brand => ({
            label: brand.label,
            key: brand.key,
            icon: brand.icon
        }));
    });

    public competitorOptions = computed<SelectItem[]>(() => {
        const brandKey = this.brandControlValue();
        if (!brandKey) return [];
        const brandCompetitors = this.dashboardService.brandsOptions().brandCompetitorsResponse?.[brandKey];
        if (!brandCompetitors?.competitors) return [];
        return brandCompetitors.competitors.map(competitor => ({
            label: competitor.label,
            key: competitor.key,
            icon: competitor.icon
        }));
    });

    public selectedBrand = computed(() => {
        const brandKey = this.brandControlValue();
        if (!brandKey) return null;
        return this.brandOptions().find(b => b.key === brandKey) || null;
    });

    public selectedCompetitors = computed(() => {
        const competitorKeys = this.competitorControlValue();
        if (!competitorKeys || competitorKeys.length === 0) return [];
        return competitorKeys
            .map(key => this.competitorOptions().find(c => c.key === key))
            .filter((c): c is SelectItem => c !== null && c !== undefined);
    });

    public hasNoCompetitors = computed(() => {
        return this.selectedBrand() !== null && this.competitorOptions().length === 0;
    });

    constructor() {
        effect(() => {
            this.selectedBrand();
            this.brandControlValue();
        });
    }

    public onContainerClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target.closest('.ng-dropdown-panel') || target.closest('app-select-menu')) {
            return;
        }
        this.isPanelOpen.set(false);
        this.isCompetitorPanelOpen.set(false);
        setTimeout(() => {
            const hasBrand = this.selectedBrand() !== null;
            const hasCompetitors = this.competitorOptions().length > 0;
            if (hasBrand && hasCompetitors) {
                this.isCompetitorPanelOpen.set(true);
            } else {
                this.isPanelOpen.set(true);
            }
        }, 0);
    }

    public onBrandSelected(brandKey: string | null): void {
        if (brandKey) {
            this.copilotService.myBrand.set(brandKey);
            this.copilotService.myCompetitors.set([]);
            this.competitorControl.setValue([]);
            setTimeout(() => {
                this.isPanelOpen.set(false);
                if (this.competitorOptions().length > 0) {
                    this.isCompetitorPanelOpen.set(true);
                }
            }, 0);
        } else {
            this.copilotService.myBrand.set(null);
            this.copilotService.myCompetitors.set([]);
        }
    }

    public onCompetitorsSelected(competitorKeys: string[] | null): void {
        if (competitorKeys) {
            this.copilotService.myCompetitors.set(competitorKeys);
        } else {
            this.copilotService.myCompetitors.set([]);
        }
        setTimeout(() => {
            this.isCompetitorPanelOpen.set(false);
        }, 0);
    }

    public onBackToBrands(event: MouseEvent): void {
        event.stopPropagation();
        this.competitorControl.setValue([]);

        if (this.competitorSelectMenu?.ngSelect) {
            this.competitorSelectMenu.ngSelect.close();
        }
        this.isCompetitorPanelOpen.set(false);
        setTimeout(() => {
            this.isPanelOpen.set(true);
        }, 100);
    }

    public onReset(event: MouseEvent): void {
        event.stopPropagation();
        this.brandControl.setValue(null);
        this.competitorControl.setValue([]);
        this.copilotService.myBrand.set(null);
        this.copilotService.myCompetitors.set([]);
        this.isPanelOpen.set(false);
        this.isCompetitorPanelOpen.set(false);
    }


    public onImageError(event: Event): void {
        const pipe = new AssetVersionPipe();
        (event.target as HTMLImageElement).src = pipe.transform('images/no-logo.png');
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        const clickedInside = this.elementRef.nativeElement.contains(event.target as Node);
        if (!clickedInside) {
            this.isPanelOpen.set(false);
            this.isCompetitorPanelOpen.set(false);
        }
    }
}
